import { useNavigate } from "react-router";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Alert, AlertDescription } from "@/shared/components/ui/alert";
import type { MatrixListItemUi } from "@/domains/matrix";
import { useDeleteMatrix } from "../hooks/use-delete-matrix";
import { extractErrorMessage } from "../lib/extract-error-message";

type DeleteMatrixDialogProps = {
  target: { collectionId: string; name: string } | null;
  matrices: MatrixListItemUi[];
  activeCollectionId: string | null;
  onClose: () => void;
};

// Shared by the rail and the toolbar, and navigates away from a deleted one.
export function DeleteMatrixDialog({
  target,
  matrices,
  activeCollectionId,
  onClose,
}: DeleteMatrixDialogProps) {
  const navigate = useNavigate();
  const deleteMatrix = useDeleteMatrix();

  function handleOpenChange(open: boolean) {
    if (open || deleteMatrix.isPending) return;
    onClose();
    deleteMatrix.reset();
  }

  function handleConfirmDelete() {
    if (!target) return;
    const { collectionId } = target;

    deleteMatrix.mutate(collectionId, {
      onSuccess: () => {
        onClose();
        if (collectionId !== activeCollectionId) return;

        const nextMatrix = matrices.find(
          (matrix) => matrix.collectionId !== collectionId,
        );
        void navigate(
          nextMatrix ? `/matrix/${nextMatrix.collectionId}` : "/matrix",
        );
      },
    });
  }

  return (
    <Dialog open={target !== null} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete matrix</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete "{target?.name}"? Its rows and their
            reports will be removed. This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        {deleteMatrix.error ? (
          <Alert variant="destructive">
            <AlertDescription>
              {extractErrorMessage(
                deleteMatrix.error,
                "Failed to delete the matrix. Please try again.",
              )}
            </AlertDescription>
          </Alert>
        ) : null}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={deleteMatrix.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirmDelete}
            disabled={deleteMatrix.isPending}
          >
            {deleteMatrix.isPending ? "Deleting..." : "Delete matrix"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
